import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useClients } from '@/features/clients/useClients';
import { useStaffAssignments, useStaffMutations } from './useStaff';
import type { StaffClinance } from '@/types/database';

interface Props {
  open: boolean;
  onClose: () => void;
  staff: StaffClinance | null;
}

export function StaffAssignModal({ open, onClose, staff }: Props) {
  const { data: allClients = [], isLoading: loadingClients } = useClients();
  const { data: currentAssignments, isLoading: loadingAssignments } = useStaffAssignments(
    staff?.id ?? '',
  );
  const { saveAssignments } = useStaffMutations();

  const [selected, setSelected] = useState<string[]>([]);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (currentAssignments) {
      setSelected(currentAssignments);
    }
  }, [currentAssignments]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const toggle = (clientId: string) => {
    setSelected((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId],
    );
  };

  const handleSave = async () => {
    if (!staff) return;
    await saveAssignments.mutateAsync({ staffId: staff.id, clientIds: selected });
    onClose();
  };

  const loading = loadingClients || loadingAssignments;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">
            Atribuir clientes — {staff?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={20} className="animate-spin text-muted-foreground" />
            </div>
          ) : allClients.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Nenhum cliente cadastrado.
            </p>
          ) : (
            <div className="max-h-80 space-y-1 overflow-y-auto">
              {allClients.map((client) => (
                <label
                  key={client.id}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-secondary"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(client.id)}
                    onChange={() => toggle(client.id)}
                    className="h-4 w-4 rounded border-border accent-primary"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">{client.name}</p>
                    {client.package && (
                      <p className="text-xs text-muted-foreground">{client.package}</p>
                    )}
                  </div>
                  <span
                    className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                      client.status === 'ativo'
                        ? 'bg-green-100 text-green-700'
                        : client.status === 'lead'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {client.status}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saveAssignments.isPending}
            className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
          >
            {saveAssignments.isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Salvando…
              </>
            ) : (
              `Salvar (${selected.length} cliente${selected.length !== 1 ? 's' : ''})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
